import { autoinject, bindable } from 'aurelia-framework';
import { PubSub } from 'lib/event';
import { AsyncErrorHandler, ViewLoadingFader } from 'lib/ui';
import { PenDto } from 'models/pen/Pen';
import { PenDimensionDto } from 'models/pen/PenDimension';
import { PenOfferDto } from 'models/pen/PenOffers';
import { PenDimensionService } from 'services/pen/pen-dimension-service';
import { PenPipeSDRService } from 'services/pen/pen-pipe-SDR-service';
import { PenPipeSizeService } from 'services/pen/pen-pipe-size-service';
import { PenService } from 'services/pen/pen-service';
import { ToastService } from 'services/toast-service';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';
import { PenDesignTypeEnum } from 'models/pen/PenDesignType';
import { NetStandardService } from 'services/net-standard-service';
import { Models } from 'models/core';

@autoinject
@connectTo()
export class PenDimensionContainer {
  @bindable
  protected penOfferId: number;
  @bindable
  protected penId: number;
  @bindable
  protected penIdentifier: string;

  protected state: ApplicationState;
  protected penOffer: PenOfferDto;
  protected hasBottomRing: boolean = false;
  protected isLoading = true;
  protected isLocked = false;
  protected isAdmin = false;

  protected isLockedExtremeHD500 = false;

  protected get isPenContext(): boolean {
    return !this.penOfferId;
  }

  protected pipeCounts = [
    { Id: 1, Name: '1', Value: 1 },
    { Id: 2, Name: '2', Value: 2 },
    { Id: 3, Name: '3', Value: 3 },
    { Id: 4, Name: '4', Value: 4 },
  ];

  protected pipeSegmentLenghtsInner = [
    { Id: 12, Name: '12', Value: 12 },
    { Id: 18, Name: '18', Value: 18 },
  ];

  protected pipeSegmentLenghtsOuter = [
    { Id: 12, Name: '12', Value: 12 },
    { Id: 18, Name: '18', Value: 18 },
  ];

  protected pipeSegmentLenghtsFirstMiddle = [
    { Id: 12, Name: '12', Value: 12 },
    { Id: 18, Name: '18', Value: 18 },
  ];

  protected pipeSegmentLenghtsSecondMiddle = [
    { Id: 12, Name: '12', Value: 12 },
    { Id: 18, Name: '18', Value: 18 },
  ];

  protected pipeSegmentLenghtsBottomRing = [
    { Id: 12, Name: '12', Value: 12 },
    { Id: 18, Name: '18', Value: 18 },
  ];

  protected pen: PenDto;
  protected penDimension: PenDimensionDto;

  constructor(
    protected element: Element,
    private penService: PenService,
    private penDimensionService: PenDimensionService,
    private toastService: ToastService,
    private penPipeSizeService: PenPipeSizeService,
    private penPipeSDRService: PenPipeSDRService,
    private netStandardService: NetStandardService,
    private pubSub: PubSub
  ) {
    this.pubSub.sub('entity-parent:updated', async () => {
      await this.penDimensionService.updatePenDimension(this.penDimension.Id, this.penDimension);
    });
  }
  @ViewLoadingFader
  protected async attached() {
    if (this.state.roles.includes('Administrator') || this.state.roles.includes('PensAdministrator')) {
      this.isAdmin = true;
    }
    await this.getPen(this.penId);
    await this.getPenDimension(this.pen.PenDimensionId);
    this.isLockedExtremeHD500 = this.penDimension.PenDesignType?.Type === PenDesignTypeEnum.ExtremeHD500 ? true : false;
    this.isLocked = this.penIdentifier && !this.isAdmin ? true : false;
    this.isLoading = false;
  }

  @AsyncErrorHandler
  async getPenDimension(id: number) {
    const { data } = await this.penDimensionService.getPenDimension(id);
    this.penDimension = data;

    this.penDimension.PenDimensionPipes.forEach((x) => {
      const placement = x.Placement;
      const length = this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength;
      const segments = this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegments;

      if (placement === 0 && !this.pipeSegmentLenghtsBottomRing.some((x) => x.Value === length)) {
        const newOption = {
          Id: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
          Name: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength?.toString(),
          Value: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
        };
        this.pipeSegmentLenghtsBottomRing.push(newOption);
      }
      if (segments == null) {
        this.calculateSegments(placement);
      }

      if (placement === 1 && !this.pipeSegmentLenghtsInner.some((x) => x.Value === length)) {
        const newOption = {
          Id: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
          Name: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength?.toString(),
          Value: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
        };
        this.pipeSegmentLenghtsInner.push(newOption);
      }
      if (segments == null) {
        this.calculateSegments(placement);
      }
      if (placement === 2 && !this.pipeSegmentLenghtsOuter.some((x) => x.Value === length)) {
        const newOption = {
          Id: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
          Name: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength?.toString(),
          Value: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
        };
        this.pipeSegmentLenghtsOuter.push(newOption);
      }
      if (segments == null) {
        this.calculateSegments(placement);
      }
      if (placement === 3 && !this.pipeSegmentLenghtsFirstMiddle.some((x) => x.Value === length)) {
        const newOption = {
          Id: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
          Name: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength?.toString(),
          Value: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
        };
        this.pipeSegmentLenghtsFirstMiddle.push(newOption);
      }
      if (segments == null) {
        this.calculateSegments(placement);
      }
      if (placement === 4 && !this.pipeSegmentLenghtsSecondMiddle.some((x) => x.Value === length)) {
        const newOption = {
          Id: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
          Name: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength?.toString(),
          Value: this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement).PipeSegmentLength,
        };
        this.pipeSegmentLenghtsSecondMiddle.push(newOption);
      }
      if (segments == null) {
        this.calculateSegments(placement);
      }
    });
  }

  @AsyncErrorHandler
  async getPen(id: number) {
    const { data } = await this.penService.getPen(id);
    this.pen = data;
  }

  protected calculateSegments(placement: number, all?: boolean) {
    if (!this.penDimension.CirumferenceInnerPipe) return;

    if (all) {
      this.penDimension.PenDimensionPipes.forEach((pipe) => {
        if (!pipe.PipeSegmentLength) return;
        const segments = this.penDimension.CirumferenceInnerPipe / pipe.PipeSegmentLength;
        const segmentsInteger = segments % 1 === 0 ? segments : Math.ceil(segments);
        pipe.PipeSegments = segmentsInteger;
      });
      return;
    }

    const pipe = this.penDimension.PenDimensionPipes.find((x) => x.Placement === placement);
    if (!pipe || !pipe.PipeSegmentLength) return;

    const segments = this.penDimension.CirumferenceInnerPipe / pipe.PipeSegmentLength;
    const segmentsInteger = segments % 1 === 0 ? segments : Math.ceil(segments);
    this.penDimension.PenDimensionPipes.filter((x) => x.Placement === placement).forEach((x) => (x.PipeSegments = segmentsInteger));
  }

  protected setPenPipeLength(value: number, placement: number) {
    if (this.isLoading) {
      setTimeout(() => {
        this.penDimension.PenDimensionPipes.filter((x) => x.Placement === placement).forEach((x) => (x.PipeSegmentLength = value));
        this.calculateSegments(placement);
      }, 500);
    } else {
      this.penDimension.PenDimensionPipes.filter((x) => x.Placement === placement).forEach((x) => (x.PipeSegmentLength = value));
      this.calculateSegments(placement);
    }
  }

  @AsyncErrorHandler
  async save() {
    await this.penDimensionService.updatePenDimension(this.penDimension.Id, this.penDimension);
    this.toastService.showSuccess('pens.penDimension.updated');
  }

  @AsyncErrorHandler
  async setPipeCount(pipeCount: number) {
    await this.penDimensionService.updatePenDimensionPipeCount(this.penDimension.Id, pipeCount);
    await this.getPenDimension(this.pen.PenDimensionId);
  }

  protected setPenPipeSizeAllPipes(id: number) {
    // if(this.penDimension.PenDimensionPipes.find(x => x.Placement === 1)){
    //   this.penDimension.PenDimensionPipes.filter(x => x.Placement === 1).forEach(x => x.PenPipeSizeId = id);
    // }
    // if(this.penDimension.PenDimensionPipes.find(x => x.Placement === 2)){
    //   this.penDimension.PenDimensionPipes.filter(x => x.Placement === 2).forEach(x => x.PenPipeSizeId = id);
    // }
  }

  protected setPenPipeSDRAllPipes(id: number) {
    // if(this.penDimension.PenDimensionPipes.find(x => x.Placement === 1)){
    //   this.penDimension.PenDimensionPipes.find(x => x.Placement === 1).PenPipeSDRId = id;
    // }
    // if(this.penDimension.PenDimensionPipes.find(x => x.Placement === 2)){
    //   this.penDimension.PenDimensionPipes.find(x => x.Placement === 2).PenPipeSDRId = id;
    // }
  }

  @AsyncErrorHandler
  async onHasBottomRingChanged(hasBottomRing: boolean) {
    await this.penDimensionService.createDeleteBottomRing(this.penDimension.Id);
    await this.getPenDimension(this.pen.PenDimensionId);
    if (hasBottomRing) {
      this.setPenPipeLength(this.penDimension.PenDimensionPipes[2].PipeSegmentLength, 2);
    }
    this.toastService.showSuccess('offer.updated');
  }

  @AsyncErrorHandler
  async getNetStandards() {
    //NetStandard RES 1821 (Chile) is temporary only visible and used in used net so hide here
    //NetStandard NS9415 Faroe Islands is only visible on service so hide here
    //Only show NS 9415-2021, so also hide NS 9415-2009
    return await this.netStandardService.getAll('?$filter=Id ne ' + Models.NetStandardId.RES_1821 + ' and Id ne ' + Models.NetStandardId.NS9415_FaroeIslands + ' and Id ne ' + Models.NetStandardId.NS9415_2009);
  }
}
